import { useCallback, useEffect, useId, useState } from "react";
import { createPortal } from "react-dom";
import { useDialogCancelFocus } from "../components/useDialogCancelFocus";
import {
  ApiError,
  LoginConnState,
  LoginRetryState,
  api,
  confirmationMatches,
  formatDateTime,
  formatTime,
  loginEnvironmentLabel,
  loginNeedsSwitch,
  loginRetryLine,
  loginStateText,
  rithmicLoginMessage,
  rithmicLogoutMessage,
  useConnection,
  useTrading,
  type RithmicLoginRecord,
  type RithmicServer,
} from "@helm/core";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { tradingSender } from "../session";
import { useDisplayZone } from "../time/useDisplayZone";
import "./rithmic.css";

export type RithmicApi = Pick<
  typeof api,
  | "rithmicServers"
  | "rithmicSystemInfo"
  | "rithmicLogins"
  | "createRithmicLogin"
  | "deleteRithmicLogin"
  | "testRithmicLogin"
  | "setRithmicTrading"
  | "stopRithmicRetry"
>;

function errorText(e: unknown): string {
  return e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e);
}

/**
 * Settings → Rithmic (ADR-0027): connect your own Rithmic login, directly in HELM.
 *
 * 1. Connect Rithmic: pick the gateway, load its system list (RequestRithmicSystemInfo, run by the
 *    gateway), pick your system (prop firms have their own), then user name + password. The
 *    password is sent once to helm-api, sealed there and never shown or returned again.
 * 2. Each stored login: Connect / Disconnect (this session's market data and order plant), Test
 *    (a real login attempt; Rithmic's exact error text), Delete. The state shown is the gateway's
 *    (it replays the login status to every new tab, so a refresh still shows Connected), and
 *    Connect on the connected login is a no-op there. The login last connected reconnects by
 *    itself after a HELM server restart until it is disconnected.
 * 3. "Allow trading" (paper, prop and live logins only): off for every new login. Turning it on
 *    needs the login's user name typed exactly and a tick that this is a real account; turning it
 *    off is one click. The gateway re-checks it before every order.
 * 4. A refused login (owner decision 2026-10-04): the gateway tries a transient refusal again, at
 *    most 5 tries in all; the status line names the next try's time (no ticking timer here, the
 *    status line's connection item counts down) and offers "Stop retrying". A credential refusal
 *    is never retried.
 */
export function RithmicPanel({
  rithmicApi = api,
  startAdding = false,
}: {
  rithmicApi?: RithmicApi;
  /** Open with the Connect Rithmic form showing (the top bar's "Add Rithmic account…"). */
  startAdding?: boolean;
}): React.JSX.Element {
  const zone = useDisplayZone();
  const [servers, setServers] = useState<RithmicServer[] | null>(null);
  const [logins, setLogins] = useState<RithmicLoginRecord[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [adding, setAdding] = useState(startAdding);
  const [enabling, setEnabling] = useState<RithmicLoginRecord | null>(null);
  const [deleting, setDeleting] = useState<RithmicLoginRecord | null>(null);
  const status = useTrading((t) => t.login);
  const gatewayUp = useConnection((c) => c.status === "connected");

  const refresh = useCallback(() => {
    rithmicApi
      .rithmicLogins()
      .then(setLogins)
      .catch((e: unknown) => {
        setError(errorText(e));
      });
  }, [rithmicApi]);

  useEffect(() => {
    refresh();
    rithmicApi
      .rithmicServers()
      .then(setServers)
      .catch((e: unknown) => {
        setError(errorText(e));
      });
  }, [refresh, rithmicApi]);

  // The gateway pushes switch changes; refetch so the row shows the stored value too.
  const pushedTrading = status?.tradingEnabled;
  useEffect(() => {
    if (pushedTrading !== undefined) refresh();
  }, [pushedTrading, refresh]);

  const act = (key: string, run: () => Promise<unknown>, after?: () => void): void => {
    setBusy(key);
    setError(null);
    setNotice(null);
    run()
      .then(() => {
        after?.();
        refresh();
      })
      .catch((e: unknown) => {
        setError(errorText(e));
      })
      .finally(() => {
        setBusy(null);
      });
  };

  const send = (body: Parameters<NonNullable<ReturnType<typeof tradingSender>>["send"]>[0]): void => {
    const s = tradingSender();
    if (!s) {
      setError("Not connected to the TradePrizm gateway");
      return;
    }
    try {
      s.send(body);
    } catch (e) {
      setError(errorText(e));
    }
  };

  // Connected, connecting (also by itself after a restart), waiting after a kick or waiting for
  // its next retry: this login holds the session, so its row offers Disconnect (which also stops
  // the retry), not a second Connect.
  const retrying = status?.retry?.state === LoginRetryState.RETRYING;
  const activeId =
    status &&
    (status.state === LoginConnState.CONNECTED ||
      status.state === LoginConnState.CONNECTING ||
      status.state === LoginConnState.KICKED ||
      retrying)
      ? status.loginId
      : null;
  const connecting = status?.state === LoginConnState.CONNECTING;
  const retry = loginRetryLine(status, Date.now(), (ms) => formatTime(ms, zone, { seconds: true }));

  return (
    <div className="sec-panel rithmic-panel" data-testid="rithmic-panel">
      <p className="muted sec-intro">
        Connect your own Rithmic login (a prop-firm, paper or live account). Charts, the DOM and the watchlist
        then use its market data, and positions and orders come from its order plant. Your password is stored
        encrypted on this server and is never shown again.
      </p>
      <p className="muted sec-intro rithmic-one-session" data-testid="rithmic-one-session">
        Rithmic allows one session per login: connecting here logs out R | Trader (or any other platform)
        signed in with the same user name, and signing in there logs TradePrizm out.
      </p>
      {status && (
        <div
          className={`banner ${statusBanner(status.state)}`}
          role={
            statusKind(status.state) === "rejected" || statusKind(status.state) === "kicked"
              ? "alert"
              : "status"
          }
          data-testid="rithmic-status"
          data-state={statusKind(status.state)}
        >
          <strong>{status.systemName || "Rithmic"}</strong> — {loginStateText(status)}
          {retry && (
            <span className="rithmic-retry" data-testid="rithmic-retry">
              {retry.text}
              {retry.action === "stop" && (
                <button
                  type="button"
                  className="btn btn-sm rithmic-reconnect"
                  disabled={busy !== null}
                  data-testid="rithmic-stop-retry"
                  onClick={() => {
                    act(`stop:${retry.loginId}`, () => rithmicApi.stopRithmicRetry(retry.loginId));
                  }}
                >
                  Stop retrying
                </button>
              )}
            </span>
          )}
          {status.state === LoginConnState.KICKED && (
            <button
              type="button"
              className="btn btn-sm btn-primary rithmic-reconnect"
              onClick={() => {
                send({ case: "rithmicLogin", value: rithmicLoginMessage(status.loginId) });
              }}
            >
              Reconnect here
            </button>
          )}
        </div>
      )}
      {error && (
        <p className="login-error" role="alert" data-testid="rithmic-error">
          {error}
        </p>
      )}
      {notice && (
        <p className="muted" role="status" data-testid="rithmic-notice">
          {notice}
        </p>
      )}
      {logins === null && !error && <p className="muted">Loading Rithmic logins…</p>}
      <ul className="sec-list" aria-label="Rithmic logins">
        {logins?.map((l) => {
          const active = activeId === l.id;
          const needsSwitch = loginNeedsSwitch(l.environment, l.system_name);
          return (
            <li key={l.id} className="sec-row rithmic-row" data-login={l.username}>
              <div className="sec-main">
                <div className="sec-name">
                  {l.label || l.system_name}
                  <span className={`badge rithmic-env rithmic-env-${l.environment}`}>
                    {loginEnvironmentLabel(l.environment)}
                  </span>
                  {active && <span className="badge badge-info">{loginStateText(status)}</span>}
                </div>
                <div className="muted sec-meta">
                  {l.system_name} · user {l.username} · added{" "}
                  {formatDateTime(Date.parse(l.created_at), zone, { weekday: false })}
                  {l.auto_connect && (
                    <span data-testid={`auto-connect-${l.username}`}>
                      {" "}
                      · reconnects after a TradePrizm restart
                    </span>
                  )}
                </div>
                {needsSwitch && (
                  <label className={`rithmic-switch${l.trading_enabled ? " on" : ""}`}>
                    <input
                      type="checkbox"
                      role="switch"
                      aria-checked={l.trading_enabled}
                      checked={l.trading_enabled}
                      disabled={busy !== null}
                      data-testid={`allow-trading-${l.username}`}
                      onChange={() => {
                        if (l.trading_enabled) {
                          // Off is one click, immediate.
                          act(`trade:${l.id}`, () => rithmicApi.setRithmicTrading(l.id, { enabled: false }));
                        } else {
                          setEnabling(l);
                        }
                      }}
                    />
                    <span>
                      Allow trading{" "}
                      {l.trading_enabled ? (
                        <strong className="rithmic-on">ON — orders are real</strong>
                      ) : (
                        <span className="muted">off (market data, exits and cancels only)</span>
                      )}
                    </span>
                  </label>
                )}
              </div>
              <div className="sec-actions">
                {active ? (
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={!gatewayUp}
                    data-testid={`disconnect-${l.username}`}
                    onClick={() => {
                      send({ case: "rithmicLogout", value: rithmicLogoutMessage(l.id) });
                    }}
                  >
                    Disconnect
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    disabled={!gatewayUp || connecting}
                    title={
                      !gatewayUp
                        ? "Not connected to the TradePrizm gateway"
                        : connecting
                          ? "Another login is connecting"
                          : undefined
                    }
                    data-testid={`connect-${l.username}`}
                    onClick={() => {
                      setError(null);
                      send({ case: "rithmicLogin", value: rithmicLoginMessage(l.id) });
                    }}
                  >
                    Connect
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled={busy !== null}
                  onClick={() => {
                    act(`test:${l.id}`, async () => {
                      const r = await rithmicApi.testRithmicLogin(l.id);
                      if (r.ok) setNotice(r.message);
                      else setError(r.message);
                    });
                  }}
                >
                  {busy === `test:${l.id}` ? "Testing…" : "Test"}
                </button>
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled={busy !== null}
                  onClick={() => {
                    setDeleting(l);
                  }}
                >
                  Delete
                </button>
              </div>
            </li>
          );
        })}
      </ul>
      {adding ? (
        <ConnectForm
          servers={servers ?? []}
          rithmicApi={rithmicApi}
          onCancel={() => {
            setAdding(false);
          }}
          onSaved={(l) => {
            setAdding(false);
            refresh();
            // Connect right away: the result (or Rithmic's refusal) shows in the status line.
            send({ case: "rithmicLogin", value: rithmicLoginMessage(l.id) });
          }}
        />
      ) : (
        <button
          type="button"
          className="btn btn-primary"
          data-testid="connect-rithmic"
          onClick={() => {
            setAdding(true);
          }}
        >
          Connect Rithmic
        </button>
      )}
      <p className="muted sec-intro">
        Rithmic may require TradePrizm to be registered (app name / conformance) before a production login is
        accepted; its refusal text is shown here as sent.
      </p>
      {enabling && (
        <EnableTradingDialog
          login={enabling}
          busy={busy !== null}
          onCancel={() => {
            setEnabling(null);
          }}
          onConfirm={(typed) => {
            const target = enabling;
            act(
              `trade:${target.id}`,
              () =>
                rithmicApi.setRithmicTrading(target.id, {
                  enabled: true,
                  confirm_username: typed,
                  acknowledge_real_account: true,
                }),
              () => {
                setEnabling(null);
              },
            );
          }}
        />
      )}
      {deleting &&
        createPortal(
          <ConfirmDialog
            title={`Delete ${deleting.label || deleting.system_name} (${deleting.username})?`}
            confirmLabel="Delete login"
            busy={busy !== null}
            onCancel={() => {
              setDeleting(null);
            }}
            onConfirm={() => {
              const target = deleting;
              act(
                `del:${target.id}`,
                () => rithmicApi.deleteRithmicLogin(target.id),
                () => {
                  setDeleting(null);
                },
              );
            }}
          >
            <p>
              The stored credentials are erased. If this login is connected it is disconnected. Your Rithmic
              account itself is not affected.
            </p>
          </ConfirmDialog>,
          document.body,
        )}
    </div>
  );
}

/** The login status line's state: connecting, connected, rejected (with Rithmic's reason) … */
function statusKind(state: LoginConnState): "connecting" | "connected" | "rejected" | "kicked" | "off" {
  switch (state) {
    case LoginConnState.CONNECTING:
      return "connecting";
    case LoginConnState.CONNECTED:
      return "connected";
    case LoginConnState.REJECTED:
      return "rejected";
    case LoginConnState.KICKED:
      return "kicked";
    default:
      return "off";
  }
}

function statusBanner(state: LoginConnState): string {
  const k = statusKind(state);
  if (k === "connected" || k === "connecting") return "banner-info";
  return k === "off" ? "banner-warn" : "banner-danger";
}

/** Typed confirmation for "Allow trading" (ADR-0027): the user name, exactly, plus the tick. */
function EnableTradingDialog({
  login,
  busy,
  onCancel,
  onConfirm,
}: {
  login: RithmicLoginRecord;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (typed: string) => void;
}): React.JSX.Element {
  const id = useId();
  const [typed, setTyped] = useState("");
  const cancelRef = useDialogCancelFocus();
  const [ack, setAck] = useState(false);
  const live = login.environment === "live";
  const ok = confirmationMatches(typed, login.username) && ack;
  // Portalled: inside a dock panel, the dock's sashes would sit on top of the dialog.
  return createPortal(
    <div className="confirm-backdrop">
      <div
        className="confirm rithmic-enable"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={`${id}-t`}
        data-testid="enable-trading-dialog"
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.stopPropagation();
            if (!busy) onCancel();
          }
        }}
      >
        <h2 className="confirm-title" id={`${id}-t`}>
          Allow trading on a {live ? "LIVE" : "real prop-firm / paper"} account?
        </h2>
        <div className="confirm-body">
          <p>
            <strong>{login.system_name}</strong> · user <strong>{login.username}</strong> is a{" "}
            {live ? "live, real-money" : "real Rithmic (prop-firm or paper)"} account. With trading allowed,
            every order you place in TradePrizm on it goes to Rithmic and the exchange. Risk limits, lockouts
            and flatten work exactly as on the mock.
          </p>
          <p>You can turn it off again with one click; exits and cancels always keep working.</p>
          <label className="field">
            <span>
              Type the user name <code>{login.username}</code> to confirm
            </span>
            <input
              className="input"
              value={typed}
              autoComplete="off"
              spellCheck={false}
              data-testid="enable-trading-confirm"
              onChange={(e) => {
                setTyped(e.target.value);
              }}
            />
          </label>
          <label className="rithmic-ack">
            <input
              type="checkbox"
              checked={ack}
              data-testid="enable-trading-ack"
              onChange={(e) => {
                setAck(e.target.checked);
              }}
            />
            <span>I understand this is a real account and orders are not simulated.</span>
          </label>
        </div>
        <div className="confirm-actions">
          <button ref={cancelRef} type="button" className="btn" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            disabled={!ok || busy}
            data-testid="enable-trading-submit"
            onClick={() => {
              onConfirm(typed);
            }}
          >
            {busy ? "Working…" : "Allow trading"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Connect Rithmic: server → system list → system → user name + password. */
function ConnectForm({
  servers,
  rithmicApi,
  onCancel,
  onSaved,
}: {
  servers: RithmicServer[];
  rithmicApi: RithmicApi;
  onCancel: () => void;
  onSaved: (l: RithmicLoginRecord) => void;
}): React.JSX.Element {
  const id = useId();
  const [serverId, setServerId] = useState(servers[0]?.id ?? "");
  const [systems, setSystems] = useState<string[] | null>(null);
  const [systemsError, setSystemsError] = useState<string | null>(null);
  const [loadingSystems, setLoadingSystems] = useState(false);
  const [system, setSystem] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const server = servers.find((s) => s.id === serverId);

  // The server list may arrive after the form opens: default to its first entry.
  const firstServer = servers[0]?.id;
  useEffect(() => {
    if (serverId === "" && firstServer !== undefined) setServerId(firstServer);
  }, [serverId, firstServer]);

  useEffect(() => {
    if (!serverId) return;
    let live = true;
    setSystems(null);
    setSystemsError(null);
    setSystem("");
    setLoadingSystems(true);
    rithmicApi
      .rithmicSystemInfo(serverId)
      .then((r) => {
        if (live) setSystems(r.system_names);
      })
      .catch((e: unknown) => {
        if (live) setSystemsError(errorText(e));
      })
      .finally(() => {
        if (live) setLoadingSystems(false);
      });
    return () => {
      live = false;
    };
  }, [serverId, rithmicApi]);

  const canSave = serverId !== "" && system.trim() !== "" && username.trim() !== "" && password !== "";
  return (
    <form
      className="rithmic-form"
      data-testid="rithmic-form"
      autoComplete="off"
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSave || saving) return;
        setSaving(true);
        setError(null);
        rithmicApi
          .createRithmicLogin({
            server_id: serverId,
            system_name: system.trim(),
            username: username.trim(),
            password,
            label,
          })
          .then((l) => {
            setPassword("");
            onSaved(l);
          })
          .catch((err: unknown) => {
            setError(errorText(err));
          })
          .finally(() => {
            setSaving(false);
          });
      }}
    >
      <h3 className="rithmic-form-title">Connect Rithmic</h3>
      <label className="field">
        <span>Server</span>
        <select
          className="input"
          value={serverId}
          data-testid="rithmic-server"
          onChange={(e) => {
            setServerId(e.target.value);
          }}
        >
          {servers.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name} ({loginEnvironmentLabel(s.environment)}) — {s.host}
            </option>
          ))}
        </select>
        {server?.note && <small className="muted">{server.note}</small>}
      </label>
      <label className="field">
        <span>System {loadingSystems && <small className="muted">loading list…</small>}</span>
        <input
          className="input"
          list={`${id}-systems`}
          value={system}
          placeholder={systems ? "Pick or type your firm's system name" : "System name"}
          data-testid="rithmic-system"
          onChange={(e) => {
            setSystem(e.target.value);
          }}
        />
        <datalist id={`${id}-systems`}>
          {systems?.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
        {systems && (
          <small className="muted" data-testid="rithmic-systems">
            {systems.length} systems from the server: {systems.join(", ")}
          </small>
        )}
        {systemsError && (
          <small className="login-error" role="alert">
            {systemsError}
          </small>
        )}
      </label>
      <label className="field">
        <span>User name (case-sensitive)</span>
        <input
          className="input"
          value={username}
          autoComplete="off"
          spellCheck={false}
          data-testid="rithmic-username"
          onChange={(e) => {
            setUsername(e.target.value);
          }}
        />
      </label>
      <label className="field">
        <span>Password</span>
        <input
          className="input"
          type="password"
          value={password}
          autoComplete="new-password"
          data-testid="rithmic-password"
          onChange={(e) => {
            setPassword(e.target.value);
          }}
        />
      </label>
      <label className="field">
        <span>Label (optional)</span>
        <input
          className="input"
          value={label}
          maxLength={80}
          onChange={(e) => {
            setLabel(e.target.value);
          }}
        />
      </label>
      {error && (
        <p className="login-error" role="alert">
          {error}
        </p>
      )}
      <div className="confirm-actions">
        <button type="button" className="btn" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={!canSave || saving}
          data-testid="rithmic-save"
        >
          {saving ? "Saving…" : "Save and connect"}
        </button>
      </div>
      <p className="muted">
        New logins never trade until you turn on “Allow trading” for them. The Rithmic Test system on the mock
        or Rithmic Test needs no switch.
      </p>
    </form>
  );
}
